import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";


const conditionCards = [
  {
    title: 'Heart Failure',
    desc: 'The heart is not pumping blood optimally — leading to fluid buildup, fatigue, breathlessness, and reduced exercise capacity.',
    iconBg: '#FADBD8',
    iconColor: '#C0392B',
    iconPath: require('../assets/symptoms/fluid.png'),
  },
  {
    title: 'Fluid Retention',
    desc: 'Puffy ankles, rapid weight gain, and breathlessness when lying flat are hallmark signs that fluid is accumulating in the body or lungs.',
    iconBg: '#E6F1FB',
    iconColor: '#185FA5',
    iconPath: require('../assets/symptoms/fluid2.png'),
  },
  {
    title: 'Mitral Valve Disease',
    desc: 'Affects the valve between the left upper and lower heart chambers. Can cause narrowing, leakage, or prolapse — with fatigue, breathlessness, and palpitations.',
    iconBg: '#EAF3DE',
    iconColor: '#3B6D11',
    iconPath: require('../assets/symptoms/mitral.png'),
  },
  {
    title: 'Aortic Valve Disease',
    desc: 'Affects blood flow out of the heart to the body. Severe cases can cause fatigue, breathlessness, palpitations, and leg swelling.',
    iconBg: '#FAEEDA',
    iconColor: '#854F0B',
    iconPath: require('../assets/symptoms/aortic.png'),
  },
];

const fluidRetentionSymptoms = [
  'Puffy feet, ankles, or legs that pit when pressed',
  'Tightness or discomfort in shoes that previously fit',
  'Rapid weight gain of two or more pounds in a single day',
  'Shortness of breath when lying flat (orthopnea)',
  'Waking up breathless during the night (paroxysmal nocturnal dyspnoea)',
  'Abdominal fullness or bloating due to fluid accumulation',
];

const virtualSupportItems = [
  'Monitoring symptom changes and new developments over time',
  'Reviewing home blood pressure or daily weight records',
  'Tracking medication side effects and tolerance',
  'Explaining echocardiogram results or laboratory findings',
  'Deciding whether urgent in-person testing is needed',
  'Planning the long-term follow-up course of action',
];

const urgentSymptomsItems = [
  'New or progressive swelling in the legs, ankles, or abdomen',
  'Shortness of breath at rest or when lying down at night',
  'Rapid unexplained weight gain',
  'Severe or worsening fatigue',
  'Fainting or near-fainting episodes',
  'Chest pain or chest tightness',
  'Rapid or irregular heartbeat accompanied by breathlessness',
];

const qaItems = [
  {
    q: 'Does heart failure mean the heart has stopped?',
    a: 'No. Heart failure means the heart is not pumping as efficiently as it should — not that it has stopped. Most people with heart failure are managed with medication and lifestyle adjustments.',
  },
  {
    q: 'Can valve disease lead to heart failure?',
    a: 'Yes. Untreated or severe valve disease can eventually cause the heart to work harder than it should, leading to heart failure symptoms over time.',
  },
  {
    q: 'When does valve disease require surgery rather than medication?',
    a: 'Severe valve disease — particularly significant stenosis or regurgitation causing symptoms — may require valve repair or replacement. The decision depends on the valve type, severity, imaging findings, and overall risk.',
  },
];

/* ─── Sub-components ────────────────────────────────────────────────── */

function HeroBanner() {
  return (
    <div className="ihb-hero">
      <img
        src={require('../assets/symptoms/heartFailure.png')}
        alt="Chest pain and cardiovascular care"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      <div className="ihb-hero-overlay" />
      <div className="ihb-hero-content">
        <span className="ihb-hero-tag">Cardiovascular Health</span>
        <h1>Heart Failure &amp; Valve Disorders</h1>
        <p>
          When breathing becomes harder, swelling accumulates, or everyday activity
          exhausts you — understanding what is happening is the first step toward
          the right care.
        </p>
      </div>
    </div>
  );
}

function Byline() {
  return (
    <div className="ihb-byline">
      <div className="ihb-byline-dot">CV</div>
      <div className="ihb-byline-text">
        <strong>Cardiovascular Physician — Dr. Siam Al Mobarak</strong>
        <span>
          Expert guidance on heart failure, cardiac valvular disorders &amp;
          cardiovascular disease management
        </span>
      </div>
    </div>
  );
}

function ConditionCard({ title, desc, iconBg, iconColor, iconPath }) {
  return (
    <div className="ihb-rhythm-card">
      <div className="ihb-icon" >
        <img src={iconPath} alt="" />
      </div>
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}

function QAItem({ q, a }) {
  return (
    <div className="ihb-qa-item">
      <div className="ihb-qa-q">{q}</div>
      <div className="ihb-qa-a">{a}</div>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="ihb-treatment-list">
      {items.map((item) => (
        <li key={item}>
          <span className="ihb-bullet" aria-hidden="true" />
          {item}
        </li>
      ))}
    </ul>
  );
}

/* ─── Main Page Component ───────────────────────────────────────────── */

export default function HeartFailureValvePage() {
  const navigate = useNavigate();
  return (
    <>
      <Helmet>
        <title>Heart Disease Treatment | Expert Cardio Care</title>
        <meta
          name="description"
          content="Advanced heart disease treatment with trusted specialists. Personalized care for heart failure disorders to improve cardiovascular health and patient outcomes."
        />
      </Helmet>

      <div className="ihb-body">

        {/* ── Hero ── */}
        <HeroBanner />

        <div className="ihb-page">

        {/* ── Byline ── */}
        <Byline />

        {/* ── Lead ── */}
        <p className="ihb-lead">
          Both heart failure and valve disorders can be deeply worrying — and both
          tend to send patients searching for answers online before they fully
          understand what is happening. An online consultation is an excellent first
          step for reviewing symptoms, addressing medication concerns, fluid
          retention, or obtaining a second opinion.
        </p>

        {/* ── Understanding Heart Failure ── */}
        <section className="ihb-section">
          <h2>Understanding Heart Failure</h2>
          <p>
            Heart failure does not mean the heart has stopped. It means the heart
            is not pumping blood as effectively as it should. This leads to fluid
            accumulation, fatigue, breathlessness, and reduced capacity for everyday
            activity. The majority of people with heart failure are managed with
            medication — and many require a combination of drugs working together.
          </p>
          <p>
            Symptoms can include shortness of breath, swelling of the ankles and
            feet, persistent fatigue, and weight gain from fluid retention. Fluid
            retention that worsens rapidly — particularly gaining more than two to
            three pounds in a day or more than five pounds in a week — is a sign
            that warrants prompt medical review. This is why virtual follow-up can
            be so valuable: patients frequently need medication reassessment,
            symptom monitoring, and guidance on when to seek in-person or emergency
            care. Cardiovascular disease management means viewing the entire picture,
            not one symptom at a time.
          </p>
        </section>

        {/* ── Condition Cards ── */}
        <section className="ihb-section">
          <h2>Conditions We Help Evaluate</h2>
          <div className="ihb-card-grid">
            {conditionCards.map((card) => (
              <ConditionCard key={card.title} {...card} />
            ))}
          </div>
        </section>

        {/* ── Fluid Retention ── */}
        <section className="ihb-section">
          <h2>Fluid Retention Symptoms That Should Not Be Ignored</h2>
          <p>
            For many individuals, swelling is the first noticeable sign of heart
            failure or valve disease — not chest pain. Fluid retention can present
            itself in several ways, and recognizing these signs early is critical to
            preventing further deterioration:
          </p>
          <BulletList items={fluidRetentionSymptoms} />
          <p style={{ marginTop: '1rem' }}>
            These symptoms may occur because the heart cannot pump blood effectively,
            allowing fluid to accumulate in the lungs or throughout the body.
            Shortness of breath, fainting, chest pain, and sudden irregular heartbeat
            are warning signs that require immediate medical attention.
          </p>
        </section>

        {/* ── Callout ── */}
        <div className="ihb-callout" role="note">
          <p>
            <strong>Important:</strong> Rapid unexplained weight gain — particularly
            more than 2–3 lbs in a single day or more than 5 lbs in one week — is a
            key warning sign of worsening fluid retention. This should not be
            dismissed or managed with self-adjustment of medication without medical
            guidance.
          </p>
        </div>

        {/* ── Valve Disease ── */}
        <section className="ihb-section">
          <h2>Valve Disease &amp; Why It Matters</h2>
          <p>
            The heart has four valves that ensure blood flows in the correct
            direction. When a valve is narrowed, leaky, or fails to open and close
            normally, blood flow is compromised. Major forms of valve disease include
            stenosis (narrowing), regurgitation (leaking), and atresia (absent
            opening). The most frequently affected are the mitral and aortic valves.
          </p>
          <p>
            These issues can gradually cause breathlessness, exhaustion, ankle
            swelling, dizziness, chest pain, or irregular heartbeat. In some
            patients, valve disease ultimately leads to the development of heart
            failure symptoms. Whether described as heart valve disease, cardiac
            valvular disorders, or valvular disorders — appropriate evaluation is
            essential because treatment depends on the specific valve, the severity,
            and the symptoms present.
          </p>
        </section>

        {/* ── Virtual Support ── */}
        <section className="ihb-section">
          <h2>How Can Virtual Support Help?</h2>
          <p>
            Symptoms can be confusing, and patients often search online or desire a
            second opinion. Others need assistance with medication changes, new
            swelling, unexplained fatigue, or interpreting test results. A virtual
            visit provides structured, focused support between formal appointments:
          </p>
          <BulletList items={virtualSupportItems} />
          <p style={{ marginTop: '1rem' }}>
            These systematic follow-ups are an important part of managing cardiac
            patients long-term. They help patients feel less uncertain while waiting
            for formal testing or specialist consultations — and often prevent
            unnecessary emergency presentations.
          </p>
        </section>

        {/* ── Treatment ── */}
        <section className="ihb-section">
          <h2>Heart Failure Treatments &amp; Valve Care</h2>
          <p>
            Treatment is always based on the specific diagnosis. For heart failure,
            commonly used medications include ACE inhibitors, ARBs, beta blockers,
            mineralocorticoid receptor antagonists, diuretics, sacubitril/valsartan,
            digoxin, and SGLT2 inhibitors. Diuretics are particularly important for
            managing fluid retention.
          </p>
          <p>
            For valve disease, treatment can involve medications to relieve symptoms,
            lower blood pressure, regulate heart rate, reduce fluid accumulation, or
            prevent blood clots. However, severe valve disease may ultimately require
            valve repair or replacement rather than medication alone. Personalized
            heart disease treatment is essential here — the most appropriate plan
            depends on symptoms, echocardiogram findings, kidney function, blood
            pressure, heart rhythm, and overall cardiovascular risk.
          </p>
        </section>

        {/* ── Q&A ── */}
        <section className="ihb-section">
          <h2>Common Questions</h2>
          <div className="ihb-qa-section">
            {qaItems.map((item) => (
              <QAItem key={item.q} {...item} />
            ))}
          </div>
        </section>

        {/* ── Urgent Box ── */}
        <div className="ihb-urgent-box" role="alert">
          <h2>When to Seek Expert Care Immediately</h2>
          <p style={{ marginBottom: '1rem' }}>
            Seek prompt consultation — or emergency care if symptoms are severe —
            when you notice any of the following:
          </p>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {urgentSymptomsItems.map((item) => (
              <li
                key={item}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '0.5rem 0',
                  borderBottom: '0.5px solid rgba(255,255,255,0.12)',
                  fontSize: 'clamp(0.88rem, 1.3vw, 0.95rem)',
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 300,
                  lineHeight: 1.6,
                  fontFamily: 'inherit',
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#E74C3C',
                    flexShrink: 0,
                    marginTop: '8px',
                  }}
                />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* ── Closing ── */}
        <section className="ihb-section">
          <h2>Comprehensive Cardiovascular Care in One Place</h2>
          <p>
            A trusted cardiologist or heart failure team can review the underlying
            cause, make safe and evidence-based treatment adjustments, and determine
            whether urgent imaging or hospital care is necessary. A cardiovascular
            clinic that brings monitoring, medication review, and ongoing
            cardiovascular disease management together in a single coordinated
            setting provides the greatest benefit to patients managing these
            complex, long-term conditions.
          </p>
        </section>

        {/* ── Footer CTA ── */}
        <div className="ihb-footer-cta">
          <p>
            Experiencing breathlessness, swelling, fatigue, or concerning symptoms
            related to your heart? Start a virtual consultation today — get clarity,
            expert guidance, and a clear path forward.
          </p>
          <button className="ihb-cta-btn" onClick={() => navigate('/consultations')}>Book a Virtual Consultation</button>
        </div>

        </div>
      </div>
    </>
  );
}
